import { Shop as ShopComponent } from '@shared/components/ui/shop';
import { SortConfig, VirtualTable } from '@components/ui/VirtualTable';
import { faBagShopping, faEye } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge } from '@shared/components/ui/badge';
import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { statusColorMap } from '../../config/orderStatusConfig';
import { Order } from '../../models/Orders';
import { SelectionToolbar } from '../interaction';
import { BulkPreparationModal, BulkPreparationModalRef, ProductLine, ProductsModal, ProductsModalRef } from '../modals';

interface TableProps {
  orders: Order[];
  isLoading?: boolean;
  className?: string;
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  sorting?: SortConfig | null;
  onSortingChange?: (sorting: SortConfig | null) => void;
  selectedColumns?: string[];
  // 🎯 Props para contadores de acciones
  cancelableCount?: number;
  preparableCount?: number;
  sendableCount?: number;
  uncancelableCount?: number;
  // 🎯 Props de selección
  selectedOrders?: Set<number>;
  onClearSelection?: () => void;
  // 🎯 Props para handlers de acciones
  onPrepareSelected?: (preparationType: 'picking_packing' | 'massive_packing') => void;
}

export const Table = React.memo(function Table({
  orders,
  fetchNextPage,
  hasNextPage = false,
  isFetchingNextPage = false,
  sorting,
  onSortingChange,
  selectedColumns,
  cancelableCount = 0,
  preparableCount = 0,
  sendableCount = 0,
  uncancelableCount = 0,
  selectedOrders,
  onClearSelection,
  onPrepareSelected,
}: TableProps) {
  const { t } = useTranslation('ordersShippedModule');
  const bulkPreparationModalRef = useRef<BulkPreparationModalRef>(null);

  // 🎯 Funciones para manejar acciones en lote
  const handleCancelSelected = useCallback(() => {
    // TODO: Implementar lógica para cancelar pedidos seleccionados
  }, []);

  const handlePrepareSelected = useCallback(() => {
    if (preparableCount > 0) {
      bulkPreparationModalRef.current?.open(preparableCount);
    }
  }, [preparableCount]);

  const handleBulkPreparationConfirm = useCallback(
    (preparationType: 'picking_packing' | 'massive_packing') => {
      // Llamar al handler real pasado desde el padre
      onPrepareSelected?.(preparationType);
    },
    [onPrepareSelected],
  );

  const handleMarkForSendSelected = useCallback(() => {
    // TODO: Implementar lógica para marcar para enviar pedidos seleccionados
  }, []);

  const handleUncancelSelected = useCallback(() => {
    // TODO: Implementar lógica para descancelar pedidos seleccionados
  }, []);

  const handleCloseSelectionToolbar = useCallback(() => {
    // Limpiar selección usando la función específica del padre
    if (onClearSelection) {
      onClearSelection();
    }
  }, [onClearSelection]);

  // Estado y referencia para ProductsModal
  const productsModalRef = useRef<ProductsModalRef>(null);
  const [modalData, setModalData] = useState<{
    orderNumber: string;
    stockMessage: string;
    products: ProductLine[];
    subtotal: number;
    shipping: number;
    discount: number;
    total: number;
  } | null>(null);

  // Handler para abrir el modal
  const handleOpenProductsModal = (order: Order) => {
    setModalData({
      orderNumber: order.ref,
      stockMessage:
        'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
      products: order.lines.map((line) => ({
        name: line.name || (line.pack ? line.pack.title : ''),
        image: line.product?.image_hash || '',
        badge: line.product?.code || (line.pack ? line.pack.title : ''),
        qty: line.qty,
        price: line.amount,
        subtotal: line.qty * line.amount,
      })),
      subtotal: order.lines.reduce((acc, line) => acc + line.qty * line.amount, 0),
      shipping: order.total_shipping,
      discount: order.total_discount,
      total: order.total,
    });
    setTimeout(() => productsModalRef.current?.open(), 0);
  };

  // Mapeo de códigos de estado a estados del dropdown

  const columns = [
    {
      header: t('table.order'),
      accessorKey: 'ref',
      size: 30,
      sortable: true,
      cell: ({ row }: any) => {
        return <div className="text-sm font-medium flex items-center gap-2">{row.original.ref}</div>;
      },
    },
    {
      header: t('table.products'),
      accessorKey: 'products',
      size: 20,
      sortable: false,
      cell: ({ row }: any) => {
        const qty = row.original.lines?.[0]?.qty ?? 0;
        const color = qty > 0 ? 'bg-success' : 'bg-error';
        return (
          <span className="text-sm font-medium flex items-center gap-2 relative">
            <button
              type="button"
              className="relative inline-flex items-center gap-2 focus:outline-none hover:opacity-70"
              onClick={() => handleOpenProductsModal(row.original)}
              title="Ver productos"
            >
              <span className="relative inline-block">
                <FontAwesomeIcon icon={faBagShopping} className="w-3 h-3" />
                <span
                  className={`absolute -top-1 -right-0 w-1.5 h-1.5 mt-2.5 rounded-full border border-black ${color}`}
                ></span>
              </span>
              {row.original.lines.length}
            </button>
          </span>
        );
      },
    },
    {
      header: t('table.shop'),
      accessorKey: 'shop',
      size: 30,
      sortable: true,
      cell: ({ row }: any) => <ShopComponent name={row.original.shop.name} src={row.original.shop.image_hash} />,
    },
    {
      header: t('table.status'),
      accessorKey: 'status',
      size: 25,
      sortable: false,
      cell: ({ row }: any) => {
        {
          /* Estado logístico del envío - Shipping logistic status */
        }
        const statusMapped = row.original.shipment?.status_mapped;

        if (!statusMapped) {
          return <Badge className="text-xs bg-card-border text-text">Sin estado logístico</Badge>;
        }

        {
          /* Mapear colores basados en el ID del status_mapped - Map colors based on status_mapped ID */
        }
        const color = statusColorMap[statusMapped.id] || {
          bg: 'bg-card-border',
          text: 'text-text',
        };

        return <Badge className={`text-xs ${color.bg} ${color.text}`}>{statusMapped.name}</Badge>;
      },
    },
    {
      header: t('table.courier'),
      accessorKey: 'courier',
      size: 30,
      sortable: false,
      cell: ({ row }: any) =>
        row.original.shipment.courier ? (
          <ShopComponent name={row.original.shipment.courier?.name} src={row.original.shipment.courier?.image_hash} />
        ) : (
          <span className="text-sm">-</span>
        ),
    },
    {
      header: t('table.address'),
      accessorKey: 'address',
      size: 30,
      sortable: false,
      cell: ({ row }: any) => {
        const address = row.original.customer.address;
        const fullAddress = `${address.zip}, ${address.city}`;

        return <span className="text-sm font-medium">{fullAddress}</span>;
      },
    },
    {
      header: '',
      accessorKey: 'actions',
      size: 20,
      sortable: false,
      cell: ({ row }: any) => {
        const hasValidId = row.original.id && row.original.id > 0;

        return (
          <div className="flex items-center justify-center">
            {hasValidId && (
              <Link
                to={`/orders/view/${row.original.id}`}
                className="text-complementary-text hover:text-text transition-colors inline-flex items-center justify-center p-2"
                title="Ver pedido"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <FontAwesomeIcon icon={faEye} className="w-4 h-4" />
              </Link>
            )}
          </div>
        );
      },
    },
  ];

  // Filtrar columnas según las seleccionadas (si se provee la prop)
  // La columna de selección siempre debe estar presente
  const filteredColumns = selectedColumns
    ? [
        // La columna de selección siempre va primero
        columns[0],
        // Luego las columnas filtradas (excluyendo la de selección)
        ...columns.slice(1).filter((col) => selectedColumns.includes(col.accessorKey)),
      ]
    : columns;

  return (
    <div className="w-full">
      {/* 🎯 Indicador de selección
      {areSomeSelected && (
        <div className="mb-2 p-2 bg-primary/10 border border-primary/20 rounded-lg">
          <span className="text-sm font-medium text-primary">
            {selectedRows.size} de {orders.length} pedidos seleccionados
          </span>
        </div>
      )} */}

      <VirtualTable
        data={orders}
        columns={filteredColumns}
        autoHeight={true}
        maxHeight={orders.length <= 10 ? `${orders.length * 70 + 120}px` : 'calc(100vh - 150px)'}
        minHeight="120px"
        rowHeight={70}
        emptyMessage={t('empty.message')}
        className="rounded-lg shadow-sm"
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        infiniteScrollThreshold={500}
        sorting={sorting}
        onSortingChange={onSortingChange}
      />

      {/* 🎯 Toolbar de selección flotante */}
      <SelectionToolbar
        selectedCount={selectedOrders?.size || 0}
        onClose={handleCloseSelectionToolbar}
        onCancelSelected={handleCancelSelected}
        onPrepareSelected={handlePrepareSelected}
        onMarkForSendSelected={handleMarkForSendSelected}
        onUncancelSelected={handleUncancelSelected}
        cancelableCount={cancelableCount}
        preparableCount={preparableCount}
        sendableCount={sendableCount}
        uncancelableCount={uncancelableCount}
      />
      {/* Modal de productos */}
      {modalData && (
        <ProductsModal
          ref={productsModalRef}
          orderNumber={modalData.orderNumber}
          stockMessage={modalData.stockMessage}
          products={modalData.products}
          subtotal={modalData.subtotal}
          shipping={modalData.shipping}
          discount={modalData.discount}
          total={modalData.total}
        />
      )}

      {/* Modal de preparación masiva */}
      <BulkPreparationModal ref={bulkPreparationModalRef} onConfirm={handleBulkPreparationConfirm} />
    </div>
  );
});
